// Field visibility utilities

export interface FieldVisibility {
  level?: 'public' | 'admin-only' | 'hidden';
  publicCanView?: boolean;
  description?: string;
}

export interface FormField {
  id: string;
  name: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  order: number;
  options?: string[];
  visibility?: FieldVisibility;
  [key: string]: any; // Allow other properties
}

export interface User {
  _id?: string;
  id?: string;
  role: string; // 'admin' | 'user' but keeping it flexible
  [key: string]: any;
}

/**
 * Filters form fields based on user role and visibility settings
 * @param fields - Array of form fields
 * @param user - Current user (null for anonymous users)
 * @param mode - 'view' or 'edit' mode
 * @returns Filtered array of fields the user can see/edit
 */
export function filterFieldsByVisibility(
  fields: FormField[],
  user: User | null,
  mode: 'view' | 'edit' = 'view'
): FormField[] {
  // Define admin-level roles that can see admin fields
  const adminRoles = [
    'super_admin', 'admin', 'form_manager', 'payment_approver',
    'submission_viewer', 'submission_editor', 'notification_manager'
  ];
  const isAdmin = adminRoles.includes(user?.role || '');

  return fields.filter(field => {
    const visibility = field.visibility || { level: 'public' };

    switch (visibility.level) {
      case 'hidden':
        // Hidden fields are only visible to admins in edit mode
        return isAdmin && mode === 'edit';

      case 'admin-only':
        if (isAdmin) {
          return true; // Admins can see admin-only fields in any mode
        }
        // Non-admins can only view (not edit) if publicCanView is enabled
        return mode === 'view' && visibility.publicCanView === true;

      case 'public':
      default:
        return true; // Public fields are visible to everyone
    }
  });
}

/**
 * Determines if a user can edit a specific field
 * @param field - The form field
 * @param user - Current user (null for anonymous users)
 * @returns Whether the user can edit this field
 */
export function canUserEditField(field: FormField, user: User | null): boolean {
  // Define admin-level roles that can edit admin fields
  const adminRoles = [
    'super_admin', 'admin', 'form_manager', 'payment_approver',
    'submission_viewer', 'submission_editor', 'notification_manager'
  ];
  const isAdmin = adminRoles.includes(user?.role || '');
  const visibility = field.visibility || { level: 'public' };

  // Check if field is explicitly set as non-editable
  if (field.editable === false) {
    return false;
  }

  // Check if field has option-based pricing - make it non-editable automatically
  if (field.pricing && field.pricing.enabled) {
    return false; // Pricing fields are automatically non-editable to prevent payment amount manipulation
  }

  switch (visibility.level) {
    case 'hidden':
      return isAdmin; // Only admins can edit hidden fields

    case 'admin-only':
      return isAdmin; // Only admins can edit admin-only fields

    case 'public':
    default:
      return true; // Everyone can edit public fields
  }
}

/**
 * Gets visibility info for display purposes
 * @param field - The form field
 * @returns Human-readable visibility information
 */
export function getFieldVisibilityInfo(field: FormField): {
  level: string;
  description: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  icon?: string;
} {
  const visibility = field.visibility || { level: 'public' };

  switch (visibility.level) {
    case 'hidden':
      return {
        level: 'Hidden',
        description: 'This field is hidden from all users except admins',
        color: 'default',
        icon: '🔒'
      };

    case 'admin-only':
      return {
        level: 'Admin Only',
        description: visibility.publicCanView 
          ? 'Only admins can edit this field, but others can view it'
          : 'Only admins can see and edit this field',
        color: 'warning',
        icon: '⚡'
      };

    case 'public':
    default:
      return {
        level: 'Public',
        description: 'Everyone can see and fill this field',
        color: 'success',
        icon: '🌐'
      };
  }
}

/**
 * Creates a filtered form data object based on user permissions
 * @param formData - Original form data
 * @param user - Current user
 * @param mode - View or edit mode
 * @returns Form data with filtered fields
 */
export function getFilteredFormData(
  formData: { fields: FormField[]; [key: string]: any },
  user: User | null,
  mode: 'view' | 'edit' = 'view'
) {
  return {
    ...formData,
    fields: filterFieldsByVisibility(formData.fields, user, mode)
  };
}

/**
 * Filters submission data to only include fields the user can see
 * @param submissionData - Raw submission data
 * @param formFields - Form field definitions
 * @param user - Current user
 * @returns Filtered submission data
 */
export function filterSubmissionData(
  submissionData: Record<string, any>,
  formFields: FormField[],
  user: User | null
): Record<string, any> {
  const visibleFields = filterFieldsByVisibility(formFields, user, 'view');
  const visibleFieldIds = new Set(visibleFields.map(f => f.id));
  
  const filtered: Record<string, any> = {};
  
  Object.keys(submissionData).forEach(key => {
    // Handle both direct field IDs and field_ prefixed keys
    const fieldId = key.startsWith('field_') ? key.substring(6) : key;
    
    if (visibleFieldIds.has(fieldId) || visibleFieldIds.has(key)) {
      filtered[key] = submissionData[key];
    }
  });
  
  return filtered;
}
